import { useCallback, useSyncExternalStore } from "react";

/**
 * ЧЕРНОВИК ОТВЕТА — СВОЙ У КАЖДОЙ БЕСЕДЫ (`s713`, Д-09: «сохраняет черновик»).
 *
 * Раньше набранное жило в состоянии экрана переписки, а экран один на все беседы:
 * начал отвечать одной клиентке, открыл другую — те же слова стояли уже в её поле, и
 * Enter отправил бы их не тому человеку. Теперь слова привязаны к номеру беседы:
 * ушёл в другую — там своё поле; вернулся — недописанное на месте. Та же беседа,
 * открытая вторым окном (карточка лида поверх «Переписок», мини-чат уведомления),
 * видит тот же черновик.
 *
 * ⛔ `s720` (приёмка 06.10.2026: «черновики сохраняются»): черновик переживает обновление
 * страницы и потерю связи. Хранится в хранилище ВКЛАДКИ (`sessionStorage`): оно живёт, пока
 * вкладка открыта, и стирается с её закрытием — текст клиентке не остаётся в браузере общего
 * рабочего компьютера после ухода человека (довод `s713` против постоянного хранилища цел).
 * Хранилище недоступно (частный режим, запрет) — черновик живёт в памяти, как раньше.
 */
const KLYUCH_HRANILISHCHA = "opsmind.chernoviki.v1";
/** Сколько бесед держим: рабочий день дежурного — десятки разговоров, не тысячи. */
const PREDEL = 200;

function prochitat(): Map<number, string> {
  try {
    const syroe = window.sessionStorage.getItem(KLYUCH_HRANILISHCHA);
    if (syroe === null) return new Map();
    const pary: unknown = JSON.parse(syroe);
    if (!Array.isArray(pary)) return new Map();

    return new Map(
      pary.filter(
        (p): p is [number, string] =>
          Array.isArray(p) && typeof p[0] === "number" && typeof p[1] === "string" && p[1] !== "",
      ),
    );
  } catch {
    return new Map();
  }
}

const chernoviki = typeof window === "undefined" ? new Map<number, string>() : prochitat();
const slushateli = new Set<() => void>();

function sohranit(): void {
  try {
    // Самые старые уходят первыми: порядок вставки Map — порядок последней правки.
    const pary = [...chernoviki.entries()].slice(-PREDEL);
    window.sessionStorage.setItem(KLYUCH_HRANILISHCHA, JSON.stringify(pary));
  } catch {
    // Хранилище переполнено или закрыто — черновик остаётся в памяти вкладки.
  }
}

// Беседа ещё грузится — поля ответа на экране нет; ключ нужен лишь для порядка хуков.
const BEZ_BESEDY = 0;

function podpisatsya(slushatel: () => void): () => void {
  slushateli.add(slushatel);

  return () => {
    slushateli.delete(slushatel);
  };
}

export function chernovikBesedy(id: number): string {
  return chernoviki.get(id) ?? "";
}

export function zapisatChernovik(id: number, novyy: string): void {
  // Удалить и вставить заново: правленая беседа становится «самой свежей» для предела.
  chernoviki.delete(id);
  if (novyy !== "") chernoviki.set(id, novyy);
  if (id !== BEZ_BESEDY) sohranit();
  slushateli.forEach((s) => s());
}

export function useChernovik(id: number | undefined): [string, (tekst: string) => void] {
  const klyuch = id ?? BEZ_BESEDY;
  const tekst = useSyncExternalStore(podpisatsya, () => chernoviki.get(klyuch) ?? "");

  const zapisat = useCallback((novyy: string) => zapisatChernovik(klyuch, novyy), [klyuch]);

  return [tekst, zapisat];
}

/**
 * КЛЮЧ НАМЕРЕНИЯ ПЕРЕЖИВАЕТ ОБРЫВ И ОБНОВЛЕНИЕ СТРАНИЦЫ (`s720`, приёмка 06.10.2026: «защита
 * повторного клика / Enter / reconnect стабильным ключом намерения, одинаковые намеренные
 * сообщения допустимы»).
 *
 * Ключ рождается на нажатие и живёт до ИТОГА этого нажатия. Раньше он лежал в памяти экрана:
 * связь оборвалась, человек обновил страницу и нажал «Отправить» ещё раз — уходил новый ключ, и
 * сервер честно слал второе сообщение. Теперь ключ с отпечатком содержимого лежит в хранилище
 * вкладки: тот же текст той же клиентке до итога — тот же ключ, и сервер отвечает итогом первой
 * отправки. Итог получен (ушло, отказ со словами) — ключ снимается, и следующее нажатие с тем же
 * текстом — новое намеренное сообщение с новым ключом.
 */
const KLYUCH_NAMERENIY = "opsmind.namereniya.v1";
/** Намерение без итога старше — уже не «то же нажатие»: человек давно видел ленту. */
const NAMERENIE_ZHIVYOT_MS = 10 * 60 * 1000;

type Namerenie = { otpechatok: string; klyuch: string; kogda: number };

function namereniya(): Namerenie[] {
  try {
    const syroe = window.sessionStorage.getItem(KLYUCH_NAMERENIY);
    const spisok: unknown = syroe === null ? [] : JSON.parse(syroe);
    if (!Array.isArray(spisok)) return [];
    const granica = Date.now() - NAMERENIE_ZHIVYOT_MS;

    return spisok.filter(
      (n): n is Namerenie =>
        typeof n === "object" && n !== null
        && typeof (n as Namerenie).otpechatok === "string"
        && typeof (n as Namerenie).klyuch === "string"
        && typeof (n as Namerenie).kogda === "number"
        && (n as Namerenie).kogda >= granica,
    );
  } catch {
    return [];
  }
}

function zapisatNamereniya(spisok: Namerenie[]): void {
  try {
    window.sessionStorage.setItem(KLYUCH_NAMERENIY, JSON.stringify(spisok.slice(-50)));
  } catch {
    // Хранилище закрыто — ключ живёт в памяти экрана, как до `s720`.
  }
}

/** Ключ незавершённого нажатия с таким содержимым; нет — новый, и он запоминается до итога. */
export function klyuchNamereniya(otpechatok: string): string {
  const spisok = namereniya();
  const est = spisok.find((n) => n.otpechatok === otpechatok);
  if (est !== undefined) return est.klyuch;

  const klyuch = crypto.randomUUID();
  zapisatNamereniya([...spisok, { otpechatok, klyuch, kogda: Date.now() }]);

  return klyuch;
}

/** Итог нажатия получен — ключ больше не нужен: следующее такое же сообщение будет новым. */
export function snyatNamerenie(otpechatok: string): void {
  zapisatNamereniya(namereniya().filter((n) => n.otpechatok !== otpechatok));
}

/** C-17: смена человека стирает и хранилище, и уже загруженную память модуля. */
export function ubratChernovikiINamereniya(): void {
  chernoviki.clear();
  slushateli.forEach((s) => s());
  try {
    window.sessionStorage.removeItem(KLYUCH_HRANILISHCHA);
    window.sessionStorage.removeItem(KLYUCH_NAMERENIY);
  } catch {
    // Закрытое хранилище не мешает очистить память вкладки.
  }
}
